/**
 * Firebase Web SDK initialisation for the App Owner admin portal.
 *
 * Talks to the same Firebase project (`sitexpens`) as the mobile
 * RN client. Uses the standard Web SDK (not the RN facade in
 * `src/lib/firebase.ts`) because this app runs in a browser.
 *
 * Auth: Google sign-in only — no phone OTP. The operator's Google
 * account must have the `role: 'app_owner'` custom claim set via
 * `scripts/grant-app-owner.ts`.
 *
 * Functions: callables in the `us-central1` region matching the
 * server. The connector wraps them so screens can call e.g.
 * `adminListSubscribers()` with type-safe payload + result.
 */
import { initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import type {
  BillingEvent,
  Dashboard,
  StorageReport,
  StudioDetail,
  StudioRow,
  UserSummary,
} from './adminTypes';

// These keys are public by design — Firebase config is shipped to
// every web client. Security comes from rules + custom claims, not
// from hiding the project id.
const firebaseConfig = {
  apiKey: 'AIzaSyCTXnw4GlafJIR1vZb9d02MLcF98nfPCyg',
  authDomain: 'sitexpens.firebaseapp.com',
  projectId: 'sitexpens',
  storageBucket: 'sitexpens.firebasestorage.app',
  messagingSenderId: '288864968221',
  appId: '1:288864968221:ios:d4d838777e1768526ff0f4',
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

// ── Auth helpers ──────────────────────────────────────────────────

const googleProvider = new GoogleAuthProvider();

export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

export { onAuthStateChanged };
export type { User };

// ── Callable wrappers ─────────────────────────────────────────────

export const callAdmin = {
  /** Dashboard KPIs, mixes, trends, attention lists. */
  getDashboard: httpsCallable<Record<string, never>, Dashboard>(functions, 'adminGetDashboard'),
  /** Every studio, enriched with owner, billing channel and activity. */
  listStudios: httpsCallable<Record<string, never>, { rows: StudioRow[]; generatedAt: number }>(
    functions,
    'adminListStudios',
  ),
  /** Full studio profile, team, projects, billing history. */
  getStudio: httpsCallable<{ orgId: string }, StudioDetail>(functions, 'adminGetStudio'),
  /** Every registered user with studios, platforms and devices. */
  listUsers: httpsCallable<Record<string, never>, { rows: UserSummary[]; generatedAt: number }>(
    functions,
    'adminListUsers',
  ),
  /** Recount every studio's storage from the R2 bucket (takes up to a
   *  minute). Also runs automatically every night at 03:00 IST. */
  recalculateStorage: httpsCallable<Record<string, never>, StorageReport>(
    functions,
    'adminRecalculateStorage',
    { timeout: 540_000 },
  ),
  /** App Store / Google Play / web payment events, last 12 months. */
  listBillingEvents: httpsCallable<
    Record<string, never>,
    { rows: BillingEvent[]; generatedAt: number }
  >(functions, 'adminListBillingEvents'),

  overrideOrgTier: httpsCallable<
    {
      orgId: string;
      tier: 'free' | 'solo' | 'studio' | 'studio_plus' | 'agency';
      expiresAt: string | null;
      note?: string;
    },
    {
      ok: true;
      razorpayCancelled?: boolean;
      razorpayCancelFailed?: boolean;
      razorpayCancelError?: string;
    }
  >(functions, 'adminOverrideOrgTier'),

  /** Refund a captured Razorpay payment + optionally cancel the
   *  related subscription. App Owner only. */
  refundRazorpayPayment: httpsCallable<
    {
      orgId: string;
      /** `pay_xxx` from the payments subcollection. */
      paymentId: string;
      /** Paise. Omit / null for a full refund. */
      amountPaise?: number | null;
      reason: string;
      alsoCancelSubscription: boolean;
    },
    {
      ok: true;
      refundId: string;
      refundStatus: string;
      refundedAmountPaise: number;
      subscriptionCancelled?: boolean;
      subscriptionCancelFailed?: boolean;
      subscriptionCancelError?: string;
    }
  >(functions, 'adminRefundRazorpayPayment'),

  updatePlanConfig: httpsCallable<
    {
      free: { maxMembers: number; maxProjects: number; maxStorageBytes: number };
      solo: { maxMembers: number; maxProjects: number; maxStorageBytes: number };
      studio: { maxMembers: number; maxProjects: number; maxStorageBytes: number };
      agency: { maxMembers: number; maxProjects: number; maxStorageBytes: number };
    },
    { ok: true }
  >(functions, 'adminUpdatePlanConfig'),

  updateAppVersion: httpsCallable<
    {
      latestIos: string;
      latestAndroid: string;
      minimumIos: string;
      minimumAndroid: string;
      releaseNotes?: string;
      updateUrlIos?: string;
      updateUrlAndroid?: string;
    },
    { ok: true }
  >(functions, 'adminUpdateAppVersion'),

  /** One-shot maintenance — populates `users/{uid}.memberOrgs` for
   *  every existing user/org pair. Safe to re-run. After this lands,
   *  the org-switch fast path in `computeUserClaims` covers every
   *  user and the legacy fallback can be removed in a cleanup PR. */
  backfillMemberOrgs: httpsCallable<
    Record<string, never>,
    {
      ok: true;
      scannedOrgs: number;
      affectedUsers: number;
      writes: number;
      sampleUserIds: string[];
    }
  >(functions, 'backfillMemberOrgs'),
};
